/**
 * HomyIcon.jsx
 * Polymorphic icon component resolving bespoke SVG glyphs from IconRegistry
 * based on the active icon theme variant.
 */

import * as React from "react";
import { cn } from "../lib/utils.js";
import { useIconTheme } from "./IconThemeContext.jsx";
import { iconRegistry } from "./registry.js";
import { initializeRegistry } from "./index.js";
import { domainMap, getDomainConfig } from "./domainMap.js";
import { CompositeActionIcon } from "./CompositeActionIcon.jsx";

const SIZE_MAP = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
};

export const HomyIcon = React.forwardRef(
  (
    {
      name,
      variant: explicitVariant,
      action = null,
      size = "md",
      color = "currentColor",
      glow = false,
      className = "",
      badgeClassName = "",
      style,
      "aria-label": ariaLabel,
      "aria-hidden": ariaHidden,
      ...props
    },
    ref
  ) => {
    const { variant: contextVariant, isGlowEnabled, colorMode } = useIconTheme();
    const activeVariant = explicitVariant || contextVariant || "spatial-bento-duotone";
    const shouldGlow = Boolean(
      glow || isGlowEnabled || activeVariant === "spatial-bento-glow"
    );

    // Resolve pixel dimensions
    const numericSize = typeof size === "number" ? size : SIZE_MAP[size] || 20;

    // Resolve semantic color token: distinguish between Tailwind classes and raw CSS color values
    let resolvedColorClass = "";
    let customColorStyle = null;

    if (color === "semantic" && colorMode === "semantic") {
      const domainConfig = getDomainConfig(name) || iconRegistry.getDomainConfig(name);
      if (domainConfig) {
        resolvedColorClass = domainConfig.colorClass;
      }
    } else if (color === "current" || color === "currentColor") {
      resolvedColorClass = "text-current";
    } else if (color && color !== "semantic") {
      if (typeof color === "string" && color.startsWith("text-")) {
        resolvedColorClass = color;
      } else {
        customColorStyle = { color };
      }
    }

    const mergedStyle = customColorStyle
      ? { ...customColorStyle, ...style }
      : style;

    // If an action modifier is requested, delegate to CompositeActionIcon
    if (action) {
      return (
        <CompositeActionIcon
          ref={ref}
          name={name}
          action={action}
          variant={activeVariant}
          size={numericSize}
          glow={shouldGlow}
          color={color}
          colorClass={resolvedColorClass}
          className={className}
          badgeClassName={badgeClassName}
          style={mergedStyle}
          aria-label={ariaLabel}
          aria-hidden={ariaHidden}
          {...props}
        />
      );
    }

    initializeRegistry();
    const GlyphComponent = iconRegistry.getGlyph(name, activeVariant);

    if (!GlyphComponent) {
      // Graceful fallback: render an empty SVG container with warning in dev
      if (
        typeof process !== "undefined" &&
        process.env &&
        process.env.NODE_ENV !== "production"
      ) {
        console.warn(
          `[HomyIcon] Glyph "${name}" not found in variant "${activeVariant}".`
        );
      }
      return (
        <svg
          ref={ref}
          width={numericSize}
          height={numericSize}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          style={mergedStyle}
          className={cn("shrink-0 select-none", className)}
          aria-hidden={ariaHidden ?? !ariaLabel}
          aria-label={ariaLabel}
          data-icon={name}
          data-fallback="true"
          {...props}
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
        </svg>
      );
    }

    return (
      <GlyphComponent
        ref={ref}
        size={numericSize}
        width={numericSize}
        height={numericSize}
        style={mergedStyle}
        className={cn(
          "shrink-0 select-none transition-colors",
          resolvedColorClass,
          shouldGlow && "filter drop-shadow-[0_0_5px_currentColor]",
          className
        )}
        aria-hidden={ariaHidden ?? !ariaLabel}
        aria-label={ariaLabel}
        data-icon={name}
        data-variant={activeVariant}
        {...props}
      />
    );
  }
);

HomyIcon.displayName = "HomyIcon";
